import { isRecord } from '@fruitful/fruitils';
import {
    CONFIRM_POST_SESSION_STEP_ID,
    type ConfirmPostSessionStepResume,
    type PostSessionWorkflowInput,
} from '@fruitful/types';
import type { WorkflowRun } from '@mastra/core/storage';
import type {
    WorkflowRunState,
    WorkflowRunStatus,
    WorkflowState,
} from '@mastra/core/workflows';
import { formatDistanceToNow } from 'date-fns';
import { type BadgeLabelVariant } from 'smoothie';

import { formatToSimpleDateString } from '../../common/utils/formatting';
import { type Member } from '../../entities/entityTypes';
import { getSoloOrJointMemberName } from '../../entities/membership/membershipUtils';

export enum WorkflowStatus {
    SUSPENDED = 'suspended',
    SUCCESS = 'success',
    FAILED = 'failed',
    RUNNING = 'running',
}

export const getPostSessionWorkflowRunTitle = (
    payload: PostSessionWorkflowInput | null,
): string => {
    if (payload == null) return 'Unknown';

    return (
        getSoloOrJointMemberName(payload.members as Array<Partial<Member>>) ??
        ''
    );
};

export const getMembershipIdFromWorklowRun = (
    run: WorkflowRun,
): string | null => {
    const input = extractWorkflowRunInput(run);

    if (input === null) return null;

    return input.membershipId;
};

export const getSessionIdFromWorkflowRun = (
    run: WorkflowRun,
): string | null => {
    const input = extractWorkflowRunInput(run);

    if (input === null) return null;

    return input.sessionId;
};

export const getSessionDateFromWorkflowRunState = (
    payload: PostSessionWorkflowInput | null,
): string => {
    if (payload == null) return 'Unknown';

    return formatToSimpleDateString(payload.sessionDate, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
};

export const getGuideEmailFromWorkflowRun = (run: WorkflowRun) => {
    const input = extractWorkflowRunInput(run);

    if (input === null) return 'Unknown';

    return input.guideEmailAddress;
};

export const getWorkflowStatusDisplayText = (
    status: WorkflowStatus,
): string => {
    switch (status) {
        case WorkflowStatus.SUSPENDED:
            return 'Ready for review';
        case WorkflowStatus.SUCCESS:
            return 'Completed';
        case WorkflowStatus.FAILED:
            return 'Failed';
        case WorkflowStatus.RUNNING:
            return 'Running';
        default:
            return status;
    }
};

export const getBadgeVariantFromWorkflowRun = (
    status: WorkflowRunStatus,
): BadgeLabelVariant => {
    const normalizedStatus = normalizeWorkflowRunStatus(status);

    switch (normalizedStatus) {
        case WorkflowStatus.SUSPENDED:
            return 'warning';
        case WorkflowStatus.FAILED:
            return 'danger';
        default:
            return 'neutral';
    }
};

export const normalizeWorkflowRunStatus = (
    status: WorkflowRunStatus,
): WorkflowStatus => {
    switch (status) {
        case 'suspended':
            return WorkflowStatus.SUSPENDED;
        case 'success':
            return WorkflowStatus.SUCCESS;
        case 'failed':
            return WorkflowStatus.FAILED;
        case 'running':
            return WorkflowStatus.RUNNING;
        default:
            return status as WorkflowStatus;
    }
};

const readConfirmFlag = (value: unknown): boolean | null | undefined => {
    if (value === true || value === false || value === null) {
        return value;
    }
    return undefined;
};

export const getConfirmStepResumePayload = (
    workflowRun: WorkflowState,
): ConfirmPostSessionStepResume => {
    const confirmStep = workflowRun.steps?.[CONFIRM_POST_SESSION_STEP_ID];
    if (confirmStep == null || Array.isArray(confirmStep)) {
        return {};
    }

    const suspendPayload = isRecord(confirmStep.suspendPayload)
        ? confirmStep.suspendPayload
        : {};
    const resumePayload = isRecord(confirmStep.resumePayload)
        ? confirmStep.resumePayload
        : {};

    return {
        confirmSummary:
            readConfirmFlag(resumePayload.confirmSummary) ??
            readConfirmFlag(suspendPayload.confirmSummary),
        confirmMembershipNotes:
            readConfirmFlag(resumePayload.confirmMembershipNotes) ??
            readConfirmFlag(suspendPayload.confirmMembershipNotes),
        confirmMessage:
            readConfirmFlag(resumePayload.confirmMessage) ??
            readConfirmFlag(suspendPayload.confirmMessage),
        confirmGoals:
            readConfirmFlag(resumePayload.confirmGoals) ??
            readConfirmFlag(suspendPayload.confirmGoals),
    };
};

export const getTimeSinceWorkflowStarted = (
    run: WorkflowRun,
): string | null => {
    if (run.createdAt == null) return null;

    try {
        const startDate = new Date(run.createdAt);
        return formatDistanceToNow(startDate, { addSuffix: true });
    } catch {
        return null;
    }
};

export const getTimeSinceLastUpdate = (run: WorkflowRun): string | null => {
    if (run.updatedAt == null) return null;

    try {
        const updateDate = new Date(run.updatedAt);
        return formatDistanceToNow(updateDate, { addSuffix: true });
    } catch {
        return null;
    }
};

export const extractWorkflowRunInput = (
    run: WorkflowRun,
): PostSessionWorkflowInput | null => {
    const runInput = (run.snapshot as WorkflowRunState)?.context?.input;

    if (!runInput) return null;

    return runInput as PostSessionWorkflowInput;
};
